import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate, useParams } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { ClerkProvider, SignIn } from '@clerk/react'
import { Shell } from './components/layout/Shell'
import { ScrollToTop } from './components/ScrollToTop'
import { AuthProvider, useAuth } from './hooks/useAuth'
import { ThemeProvider } from './hooks/useTheme'
import { ToastProvider } from './components/ui/Toast'
import { SpringLoader } from './components/ui/Loaders'
import { ErrorBoundary } from './components/ui/ErrorBoundary'
import { RoleGuard } from './components/RoleGuard'
import { queryClient } from './lib/queryClient'
import { HIDE_LEGACY_PAGES, LANDING_PATH, isHidden } from './config/legacy-pages'

// Clerk publishable key — set in .env or Cloudflare Pages env vars
const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY
if (!CLERK_PUBLISHABLE_KEY) {
  throw new Error('Missing VITE_CLERK_PUBLISHABLE_KEY environment variable')
}

// Eager load - always needed
import Dashboard from './pages/Dashboard'

/**
 * Lazy load with retry - handles chunk loading failures gracefully
 * When a chunk fails to load (e.g., after deployment), retry up to 3 times
 * with exponential backoff before giving up
 */
function lazyWithRetry(importFn, retries = 3, delay = 1000) {
  return lazy(() => {
    const attempt = (retriesLeft) => {
      return importFn().catch((error) => {
        if (retriesLeft > 0) {
          // Wait before retrying with exponential backoff
          return new Promise((resolve) => {
            setTimeout(() => {
              resolve(attempt(retriesLeft - 1))
            }, delay * (retries - retriesLeft + 1))
          })
        }
        // All retries failed, throw the error to be caught by ErrorBoundary
        throw error
      })
    }
    return attempt(retries)
  })
}

// Lazy load with retry - only when navigated to
const CandidateProfile = lazyWithRetry(() => import('./pages/CandidateProfile'))
const CandidateCampaign = lazyWithRetry(() => import('./pages/CandidateCampaign'))
const BusinessCampaign = lazyWithRetry(() => import('./pages/BusinessCampaign'))
const Clients = lazyWithRetry(() => import('./pages/Clients'))
const ClientDetail = lazyWithRetry(() => import('./pages/ClientDetail'))
const Campaigns = lazyWithRetry(() => import('./pages/Campaigns'))
const CampaignDetail = lazyWithRetry(() => import('./pages/CampaignDetail'))
// Leads page removed - use ClientDetail LeadsTab instead
const Prospects = lazyWithRetry(() => import('./pages/Prospects'))
const ProspectDetail = lazyWithRetry(() => import('./pages/ProspectDetail'))
// Onboarding consolidated into Clients page (tab=onboarding)
const Support = lazyWithRetry(() => import('./pages/Support'))
const Replies = lazyWithRetry(() => import('./pages/Replies'))
const UnmatchedReplies = lazyWithRetry(() => import('./pages/UnmatchedReplies'))
// Errors, Queues, Quarantine absorbed into Observability tabs (Phase 4 — lazy-loaded within Observability.jsx)
const KillSwitch = lazyWithRetry(() => import('./pages/KillSwitch'))
const Observability = lazyWithRetry(() => import('./pages/Observability'))
// LiveActivity killed (Phase 4 Effort C) — redirect at /live route
const Reports = lazyWithRetry(() => import('./pages/Reports'))
const Billing = lazyWithRetry(() => import('./pages/Billing'))
const Templates = lazyWithRetry(() => import('./pages/Templates'))
// EmailPreview killed (Phase 4 Effort C) — redirect at /email-preview route
// /email-accounts shows the full page (EmailAccounts) while HIDE_LEGACY_PAGES is false,
// and only the Engine panels (EmailAccountsEngine) when it is true.
const EmailAccounts = lazyWithRetry(HIDE_LEGACY_PAGES ? () => import('./pages/EmailAccountsEngine') : () => import('./pages/EmailAccounts'))
const BlockList = lazyWithRetry(() => import('./pages/BlockList'))
const NicheBriefs = lazyWithRetry(() => import('./pages/NicheBriefs'))
// IndustryPlaybooks + PlaybookDetail killed (Phase 8 → merged into Industries)
const IndustriesPage = lazyWithRetry(() => import('./pages/IndustriesPage'))
const IndustryDetail = lazyWithRetry(() => import('./pages/IndustryDetail'))
const SubIndustryDetail = lazyWithRetry(() => import('./pages/SubIndustryDetail'))
const NicheBriefDetail = lazyWithRetry(() => import('./pages/NicheBriefDetail'))
const NicheTemplates = lazyWithRetry(() => import('./pages/NicheTemplates'))
// OutboundPipeline killed (Phase 4 Effort C) — redirect at /outbound-pipeline route
const Portal = lazyWithRetry(() => import('./pages/Portal'))
const Studio = lazyWithRetry(() => import('./pages/Studio'))
const Contracts = lazyWithRetry(() => import('./pages/Contracts'))
const Analytics = lazyWithRetry(() => import('./pages/Analytics'))
// Deliverability killed (Phase 4 Effort C) — absorbed into EmailAccounts tabs
// Compare killed (Phase 4 Effort C) — redirect at /compare route
const Costs = lazyWithRetry(() => import('./pages/Costs'))
// Cache absorbed into Observability tabs (Phase 4 — lazy-loaded within Observability.jsx)
// FeatureFlags absorbed into Settings page (Phase 4 — redirect at /feature-flags route)
// Notifications absorbed into Settings page (Phase 4 — redirect at /notifications route)
const Settings = lazyWithRetry(() => import('./pages/Settings'))
const EngineScreen = lazyWithRetry(() => import('./pages/EngineScreen'))
const EngineSettings = lazyWithRetry(() => import('./pages/EngineSettings'))
const Docs = lazyWithRetry(() => import('./pages/Docs'))
const SubjectLineTemplates = lazyWithRetry(() => import('./pages/SubjectLineTemplates'))
// ICPTemplates killed (Phase 8 — table dropped, merged into Industries Sub-Industries tab)
const Quality = lazyWithRetry(() => import('./pages/Quality'))
const NotFound = lazyWithRetry(() => import('./pages/NotFound'))

// Redirect helper: /playbooks/:id → /industries/:id (Phase 8)
function PlaybookRedirect() {
  const { playbookId } = useParams()
  return <Navigate to={`/industries/${playbookId}`} replace />
}

// Hidden legacy routes go to the landing page instead of rendering a failing screen.
function gate(path, element) {
  return isHidden(path) ? <Navigate to={LANDING_PATH} replace /> : element
}

// Page loading fallback
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <SpringLoader size="md" text="Loading..." />
    </div>
  )
}

// Auth loading screen
function LoadingScreen() {
  return (
    <div className="min-h-screen bg-(--background) flex items-center justify-center">
      <SpringLoader size="lg" text="Loading Marketing Engine..." />
    </div>
  )
}

// Clerk sign-in screen (replaces Login page)
// Branding configured in Clerk Dashboard: logo, primary color #864AEC
// Code-side appearance tweaks below for dark mode compat
function ClerkSignInScreen() {
  return (
    <div className="min-h-screen bg-(--background) flex flex-col items-center justify-center gap-8">
      {/* Logo above sign-in card */}
      <div className="flex items-center gap-3">
        <svg viewBox="0 0 128 128" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-10 h-10">
          <ellipse cx="64" cy="64" rx="52" ry="24" transform="rotate(-30 64 64)" stroke="#864AEC" strokeWidth="8" strokeLinecap="round"/>
          <ellipse cx="64" cy="64" rx="52" ry="24" transform="rotate(30 64 64)" stroke="#864AEC" strokeWidth="8" strokeLinecap="round" opacity="0.6"/>
          <circle cx="64" cy="64" r="10" fill="#864AEC"/>
        </svg>
        <span className="text-xl font-bold text-(--foreground) tracking-tight">Clairvos</span>
      </div>
      <SignIn
        appearance={{
          variables: {
            colorPrimary: '#864AEC',
            colorTextOnPrimaryBackground: '#ffffff',
          },
          elements: {
            rootBox: 'mx-auto',
            card: 'shadow-xl border border-(--border) bg-(--card)',
            headerTitle: 'text-(--foreground)',
            headerSubtitle: 'text-(--muted-foreground)',
            socialButtonsBlockButton: 'border-(--border)',
            formFieldInput: 'bg-(--background) border-(--border) text-(--foreground)',
            footerActionLink: 'text-[#864AEC] hover:text-[#6D28D9]',
          }
        }}
      />
      <p className="text-xs text-(--muted-foreground)">Marketing Engine</p>
    </div>
  )
}

// Protected routes wrapper
function ProtectedRoutes() {
  const { isAuthenticated, isLoading } = useAuth()
  
  if (isLoading) {
    return <LoadingScreen />
  }
  
  if (!isAuthenticated) {
    return <ClerkSignInScreen />
  }
  
  return (
    <Shell>
      <ScrollToTop />
      <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Redirect root to the landing page (the Engine page) */}
            <Route path="/" element={<Navigate to={LANDING_PATH} replace />} />
            
            {/* Overview */}
            <Route path="/dashboard" element={gate("/dashboard", <Dashboard />)} />
            
            {/* Operations — admin + operator */}
            <Route path="/candidate-profiles/:profileId" element={gate("/candidate-profiles/:profileId", <CandidateProfile />)} />
            <Route path="/outreach-campaigns/:campaignId" element={gate("/outreach-campaigns/:campaignId", <CandidateCampaign />)} />
            <Route path="/business-campaigns" element={gate("/business-campaigns", <BusinessCampaign />)} />
            <Route path="/business-campaigns/:campaignId" element={gate("/business-campaigns/:campaignId", <BusinessCampaign />)} />
            <Route path="/clients" element={gate("/clients", <Clients />)} />
            <Route path="/clients/:clientId" element={gate("/clients/:clientId", <ClientDetail />)} />
            <Route path="/campaigns" element={gate("/campaigns", <Campaigns />)} />
            <Route path="/campaigns/upload" element={gate("/campaigns/upload", <Campaigns />)} />
            <Route path="/campaigns/:campaignId" element={gate("/campaigns/:campaignId", <CampaignDetail />)} />
            <Route path="/email-accounts" element={gate("/email-accounts", <EmailAccounts />)} />
            <Route path="/blocklist" element={gate("/blocklist", <BlockList />)} />
            <Route path="/prospects" element={gate("/prospects", <Prospects />)} />
            <Route path="/contracts" element={gate("/contracts", <Contracts />)} />
            <Route path="/prospects/:prospectId" element={gate("/prospects/:prospectId", <ProspectDetail />)} />
            {/* Industry (Phase 8 — replaces IndustryPlaybooks) */}
            <Route path="/industries" element={gate("/industries", <IndustriesPage />)} />
            <Route path="/industries/:playbookId" element={gate("/industries/:playbookId", <IndustryDetail />)} />
            <Route path="/industries/:playbookId/sub/:subIndustryId" element={gate("/industries/:playbookId/sub/:subIndustryId", <SubIndustryDetail />)} />
            {/* Old playbooks routes redirect to /industries */}
            <Route path="/playbooks" element={gate("/playbooks", <Navigate to="/industries" replace />)} />
            <Route path="/playbooks/:playbookId" element={gate("/playbooks/:playbookId", <PlaybookRedirect />)} />
            <Route path="/niche-briefs" element={gate("/niche-briefs", <NicheBriefs />)} />
            <Route path="/niche-briefs/:nicheId" element={gate("/niche-briefs/:nicheId", <NicheBriefDetail />)} />
            <Route path="/niche-briefs/:nicheId/templates" element={gate("/niche-briefs/:nicheId/templates", <NicheTemplates />)} />
            {/* ICP Templates killed (Phase 8) — table dropped, redirect to Industries */}
            <Route path="/icp-templates" element={gate("/icp-templates", <Navigate to="/industries" replace />)} />
            <Route path="/icp-templates/:id" element={gate("/icp-templates/:id", <Navigate to="/industries" replace />)} />
            {/* OutboundPipeline killed (Phase 4 Effort C) — redirect to Lead Discovery */}
            <Route path="/outbound-pipeline" element={gate("/outbound-pipeline", <Navigate to="/niche-briefs" replace />)} />
            {/* Subject lines absorbed into Industries (Phase 8) */}
            <Route path="/subject-lines" element={gate("/subject-lines", <Navigate to="/industries" replace />)} />
            <Route path="/onboarding" element={gate("/onboarding", <Navigate to="/clients?tab=onboarding" replace />)} />
            <Route path="/support" element={gate("/support", <Support />)} />
            <Route path="/replies" element={gate("/replies", <EngineScreen />)} />
            <Route path="/legacy/replies" element={gate("/legacy/replies", <Replies />)} />
            <Route path="/replies/unmatched" element={gate("/replies/unmatched", <UnmatchedReplies />)} />

            {/* Engine screens not connected yet: one page, chosen by the route (config/engine-screens.js) */}
            <Route path="/approvals/pulls" element={gate("/approvals/pulls", <EngineScreen />)} />
            <Route path="/approvals/drafts" element={gate("/approvals/drafts", <EngineScreen />)} />
            <Route path="/approvals/release" element={gate("/approvals/release", <EngineScreen />)} />
            <Route path="/apply-jobs" element={gate("/apply-jobs", <EngineScreen />)} />
            <Route path="/lead-finder" element={gate("/lead-finder", <EngineScreen />)} />
            <Route path="/leads" element={gate("/leads", <EngineScreen />)} />
            <Route path="/sequences" element={gate("/sequences", <EngineScreen />)} />
            <Route path="/research" element={gate("/research", <EngineScreen />)} />
            <Route path="/sending-control" element={gate("/sending-control", <EngineScreen />)} />
            <Route path="/do-not-contact" element={gate("/do-not-contact", <EngineScreen />)} />
            <Route path="/connections" element={gate("/connections", <EngineScreen />)} />
            <Route path="/accounts" element={gate("/accounts", <EngineScreen />)} />
            <Route path="/audit-log" element={gate("/audit-log", <EngineScreen />)} />
            <Route path="/people" element={gate("/people", <EngineScreen />)} />
            
            {/* System */}
            {/* Errors/Queues/Quarantine absorbed into Observability tabs (Phase 4) */}
            <Route path="/errors" element={gate("/errors", <Navigate to="/observability?tab=errors" replace />)} />
            <Route path="/queues" element={gate("/queues", <Navigate to="/observability?tab=queues" replace />)} />
            <Route path="/quarantine" element={gate("/quarantine", <Navigate to="/observability?tab=quarantine" replace />)} />
            <Route path="/observability" element={gate("/observability", <Observability />)} />
            <Route path="/quality" element={gate("/quality", <Quality />)} />
            {/* LiveActivity killed (Phase 4 Effort C) */}
            <Route path="/live" element={gate("/live", <Navigate to="/observability" replace />)} />
            <Route path="/reports" element={gate("/reports", <EngineScreen />)} />
            <Route path="/legacy/reports" element={gate("/legacy/reports", <Reports />)} />

            {/* Admin-only routes — wrapped with RoleGuard */}
            <Route path="/kill-switch" element={gate("/kill-switch", <RoleGuard allow={['admin']}><KillSwitch /></RoleGuard>)} />
            <Route path="/billing" element={gate("/billing", <RoleGuard allow={['admin']}><Billing /></RoleGuard>)} />
            {/* Feature Flags absorbed into Settings (Phase 4) */}
            <Route path="/feature-flags" element={gate("/feature-flags", <RoleGuard allow={['admin']}><Navigate to="/legacy/settings?section=flags" replace /></RoleGuard>)} />
            
            {/* Portal */}
            <Route path="/portal" element={gate("/portal", <Portal />)} />
            <Route path="/studio" element={gate("/studio", <Studio />)} />

            {/* Settings */}
            <Route path="/templates" element={gate("/templates", <Templates />)} />
            {/* EmailPreview killed (Phase 4 Effort C) */}
            <Route path="/email-preview" element={gate("/email-preview", <Navigate to="/email-accounts" replace />)} />
            <Route path="/analytics" element={gate("/analytics", <Analytics />)} />
            {/* Deliverability killed (Phase 4 Effort C) — absorbed into EmailAccounts */}
            <Route path="/deliverability" element={gate("/deliverability", <Navigate to="/email-accounts" replace />)} />
            {/* Compare killed (Phase 4 Effort C) */}
            <Route path="/compare" element={gate("/compare", <Navigate to="/analytics" replace />)} />
            <Route path="/costs" element={gate("/costs", <EngineScreen />)} />
            <Route path="/legacy/costs" element={gate("/legacy/costs", <Costs />)} />
            {/* Cache absorbed into Observability (Phase 4) */}
            <Route path="/cache" element={gate("/cache", <Navigate to="/observability?tab=cache" replace />)} />
            {/* Notifications absorbed into Settings (Phase 4) */}
            <Route path="/notifications" element={gate("/notifications", <Navigate to="/legacy/settings?section=notifications" replace />)} />
            <Route path="/settings" element={gate("/settings", <EngineSettings />)} />
            <Route path="/legacy/settings" element={gate("/legacy/settings", <Settings />)} />
            <Route path="/docs" element={gate("/docs", <Docs />)} />
            
            {/* 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </Shell>
  )
}

export default function App() {
  return (
    <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AuthProvider>
            <ToastProvider>
              <ProtectedRoutes />
            </ToastProvider>
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ClerkProvider>
  )
}
